import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  type ReactNode,
} from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { authApi, apiFetchRaw, type UserSummary } from './api';
import { applyModuleConfig, resetModuleTree } from '@/shared/modules/store';
import { canSeeTile as canSeeTileImpl, hasRole as hasRoleImpl, hasTool as hasToolImpl } from './visibility';
import { clearShellSessionMemory } from '@/shell/moduleNav';
import { resetAgentFeatures } from '@/features/coportal/agents/agentFeatures';
import { loadStaffCompany } from '@/shared/companyBoot';

interface AuthContextValue {
  user: UserSummary | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<void>;
  /** Agent Portal sign-in by username (see authApi.agentLogin). */
  agentLogin: (username: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  hasPage: (slug: string) => boolean;
  hasTool: (id: string) => boolean;
  hasRole: (role: string) => boolean;
  canSeeTile: (slug: string) => boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

const ME_KEY = ['auth', 'me'] as const;
const MODULES_KEY = ['modules'] as const;

/**
 * The tenant's module config (brief §4.3), applied to the module store as it
 * arrives, so the shell's first paint already has the tenant's modules,
 * labels and switches. A failure (an older API, a network blip, no answer
 * within 5 s) leaves the catalog defaults: the Asian Trails tree, never an
 * empty one, and never a loader that stays up.
 */
/** Sign-in never waits longer than this for the module config. */
const MODULES_TIMEOUT_MS = 5_000;

async function loadModules(): Promise<true> {
  // The staff fields of the company profile (Admin ▸ Company) arrive with
  // the module tree, so the shell paints with both. Never throws.
  const company = loadStaffCompany();
  try {
    const res = await Promise.race([
      apiFetchRaw<{ ok?: boolean; config?: unknown; custom?: unknown; unavailable?: unknown }>('/modules'),
      new Promise<never>((_, reject) => setTimeout(() => reject(new Error('modules timeout')), MODULES_TIMEOUT_MS)),
    ]);
    // `custom`: the published custom modules and what this person may do in
    // each (brief §5.4); absent when the tenant has none.
    // `unavailable`: pages still waiting for a setup (WhatsApp before a number
    // is connected); absent when there is none.
    applyModuleConfig(res?.config ?? null, res?.custom ?? [], res?.unavailable ?? {});
  } catch {
    resetModuleTree();
  }
  await company;
  return true;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();

  const meQuery = useQuery({
    queryKey: ME_KEY,
    queryFn: () => authApi.me(),
    staleTime: 60_000,
    retry: false,
    refetchOnWindowFocus: true,
  });

  const signedIn = meQuery.data?.user ?? null;
  // Staff only: an agent token is confined to the Agent Portal's endpoints.
  const modulesQuery = useQuery({
    queryKey: [...MODULES_KEY, signedIn?.id ?? null],
    queryFn: loadModules,
    enabled: !!signedIn && signedIn.role !== 'agent',
    staleTime: 5 * 60_000,
    retry: false,
    refetchOnWindowFocus: false,
  });

  const loginMut = useMutation({
    mutationFn: ({ email, password }: { email: string; password: string }) =>
      authApi.login(email, password),
    onSuccess: (data) => {
      // A tab can outlive a session. The shell's tab-local memory (the last
      // module visited, which drives the Hot Plate target) belongs to whoever
      // was signed in when it was written, so it is dropped on BOTH ends of
      // an auth transition rather than inherited by the next user.
      clearShellSessionMemory();
      qc.setQueryData(ME_KEY, {
        user: data.user,
        expiresAt: data.expiresAt,
      });
    },
  });

  const agentLoginMut = useMutation({
    mutationFn: ({ username, password }: { username: string; password: string }) =>
      authApi.agentLogin(username, password),
    onSuccess: (data) => {
      clearShellSessionMemory();
      qc.setQueryData(ME_KEY, {
        user: data.user,
        expiresAt: data.expiresAt,
      });
    },
  });

  const logoutMut = useMutation({
    mutationFn: () => authApi.logout(),
    onSettled: () => {
      clearShellSessionMemory();
      resetModuleTree();
      // The next person may be an Agent Portal user: back to the key-agent rule.
      resetAgentFeatures();
      qc.setQueryData(ME_KEY, null);
      qc.clear();
    },
  });

  const login = useCallback(
    async (email: string, password: string) => {
      await loginMut.mutateAsync({ email, password });
    },
    [loginMut],
  );

  const agentLogin = useCallback(
    async (username: string, password: string) => {
      await agentLoginMut.mutateAsync({ username, password });
    },
    [agentLoginMut],
  );

  const logout = useCallback(async () => {
    await logoutMut.mutateAsync();
  }, [logoutMut]);

  const user = signedIn;
  // The shell waits for the module tree as well as for the person, so the
  // sidebar never paints Asian Trails' modules and then the tenant's.
  const modulesPending = !!user && user.role !== 'agent' && modulesQuery.isPending;
  const modulesVersion = modulesQuery.dataUpdatedAt;

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      isLoading: meQuery.isLoading || modulesPending,
      isAuthenticated: !!user,
      login,
      agentLogin,
      logout,
      hasPage: (slug: string) =>
        !!user && (user.allowedPages?.includes(slug) ?? false),
      hasTool: (id: string) => hasToolImpl(id, user),
      hasRole: (role: string) => hasRoleImpl(role, user),
      canSeeTile: (slug: string) => canSeeTileImpl(slug, user),
    }),
    // modulesVersion: a new tree re-renders every `canSeeTile` consumer.
    [user, meQuery.isLoading, modulesPending, modulesVersion, login, agentLogin, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}

/** Same as useAuth, but null outside an AuthProvider (panels also rendered standalone in tests). */
export function useOptionalAuth(): AuthContextValue | null {
  return useContext(AuthContext);
}
